import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formsApi } from '../api/formsApi';
import { config } from '../config';
import type { FormAccessMode, FormSummary } from '../api/types';

/** Small line-icon wrapper (Feather-style, inherits colour via currentColor). */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const EditIcon = () => (
  <Icon>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </Icon>
);

const SettingsIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H1a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V1a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H23a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Icon>
);

const SubmissionsIcon = () => (
  <Icon>
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </Icon>
);

export function FormList() {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newAccess, setNewAccess] = useState<FormAccessMode>('Private');

  useEffect(() => {
    formsApi
      .list()
      .then(setForms)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load forms'))
      .finally(() => setLoading(false));
  }, []);

  async function createForm(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) {
      return;
    }
    try {
      const created = await formsApi.create({ title: newTitle.trim(), accessMode: newAccess });
      navigate(`/forms/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create form');
    }
  }

  return (
    <div className="page">
      <h1>Forms</h1>

      <form className="create-form" onSubmit={createForm}>
        <input
          type="text"
          placeholder="New form title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <select value={newAccess} onChange={(e) => setNewAccess(e.target.value as FormAccessMode)}>
          <option value="Private">Private</option>
          <option value="Public">Public</option>
          <option value="SecretLink">Secret link</option>
        </select>
        <button type="submit">Create</button>
      </form>

      {error && <p className="error">{error}</p>}
      {loading ? (
        <p>Loading…</p>
      ) : (
        <table className="forms-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Access</th>
              <th>Published</th>
              <th>Draft</th>
              <th className="icon-col">Edit</th>
              <th className="icon-col">Settings</th>
              <th className="icon-col">Responses</th>
            </tr>
          </thead>
          <tbody>
            {forms.map((form) => (
              <tr key={form.id}>
                <td><Link to={`/forms/${form.id}`} className="form-title-link">{form.title}</Link></td>
                <td>{form.accessMode}</td>
                <td>{form.publishedVersionNumber ? `v${form.publishedVersionNumber}` : '—'}</td>
                <td>{form.hasDraft ? 'Yes' : '—'}</td>
                <td className="icon-col">
                  {form.hasDraft && (
                    <a
                      className="icon-action"
                      title="Edit fields in the Form Builder"
                      aria-label="Edit fields"
                      href={`${config.formbuilderUrl.replace(/\/$/, '')}/?formId=${form.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <EditIcon />
                    </a>
                  )}
                </td>
                <td className="icon-col">
                  <Link className="icon-action" title="Settings" aria-label="Settings" to={`/forms/${form.id}`}>
                    <SettingsIcon />
                  </Link>
                </td>
                <td className="icon-col">
                  <Link
                    className="icon-action"
                    title="Submissions"
                    aria-label="Submissions"
                    to={`/forms/${form.id}/submissions`}
                  >
                    <SubmissionsIcon />
                  </Link>
                </td>
              </tr>
            ))}
            {forms.length === 0 && (
              <tr>
                <td colSpan={7}>No forms yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
